import React from 'react';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CollapsibleChartGroup from './CollapsibleChartGroup';
import { useStationsContext } from '../../context/StationsContext';

const RangeTrendCandleChart = ({ data, loading, selectedStations }) => {
    const theme = useTheme();
    const { getStationName } = useStationsContext();

    // Helper to generate dynamic parameters (similar to MultiStationTrendChart)
    const generateGroupParams = (baseParams, stations) => {
        const params = [];
        const DASH_PATTERNS = ["", "10 5", "2 2", "10 2 2 2", "5 5", "20 5", "10 5 2 5 2 5", "5 2 5 2", "2 5", "15 3 3 3"];

        stations.forEach((station, idx) => {
            baseParams.forEach(bp => {
                const stationDisplay = getStationName ? getStationName(station) : station;
                params.push({
                    key: `${station}_${bp.key}`,
                    label: `${stationDisplay} - ${bp.label}`,
                    shortLabel: bp.shortLabel || bp.label,
                    color: bp.color,
                    strokeWidth: bp.strokeWidth || 3,
                    strokeDasharray: DASH_PATTERNS[idx % DASH_PATTERNS.length],
                    defaultOn: bp.defaultOn
                });
            });
        });
        return params;
    };

    const generateStationGroups = (baseParams, stations) => {
        return stations.map(station => ({
            label: getStationName ? getStationName(station) : station,
            paramKeys: baseParams.map(bp => `${station}_${bp.key}`)
        }));
    };

    if (loading) {
        return (
            <Box
                className="glass-panel flex-center"
                sx={{
                    height: '100%',
                    minHeight: '400px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}
            >
                <Typography color="text.secondary">Loading range data...</Typography>
            </Box>
        );
    }

    const rawData = data || [];

    // 2m Depth Parameters (Standardized with Daily Data)
    const params2m = [
        { key: 'at2mDegC', label: 'Temperature (°C)', color: '#c30a0aff', strokeWidth: 3, defaultOn: true },
        { key: 'rh2mPer', label: 'Humidity (%)', color: '#1565C0', strokeWidth: 3, defaultOn: true },
        { key: 'rainFallMm', label: 'Rainfall (mm)', color: '#311B92', strokeWidth: 3, defaultOn: true },
        { key: 'avgNetRadiationwBym2', label: 'Net Rad (W/m²)', color: '#eee30dff', strokeWidth: 3, defaultOn: true },
        { key: 'ws2mMps', label: 'Wind Spd (m/s)', color: '#2E7D32', strokeWidth: 3, defaultOn: true },
        { key: 'wd2mDeg', label: 'Wind Dir (°)', color: '#00897B', strokeWidth: 3, defaultOn: true },
        { key: 'ap2mHpa', label: 'Pressure (hPa)', color: '#7B1FA2', strokeWidth: 3, defaultOn: true }
    ];

    // 4m Depth Parameters
    const params4m = [
        { key: 'at4mDegC', label: 'Temperature (°C)', color: '#c30a0aff', strokeWidth: 3, defaultOn: true },
        { key: 'rh4mPer', label: 'Humidity (%)', color: '#1565C0', strokeWidth: 3, defaultOn: true },
        { key: 'rainFallMm', label: 'Rainfall (mm)', color: '#311B92', strokeWidth: 3, defaultOn: true },
        { key: 'avgNetRadiationwBym2', label: 'Net Rad (W/m²)', color: '#eee30dff', strokeWidth: 3, defaultOn: true },
        { key: 'ws4mMps', label: 'Wind Spd (m/s)', color: '#2E7D32', strokeWidth: 3, defaultOn: true },
        { key: 'wd4mDeg', label: 'Wind Dir (°)', color: '#00897B', strokeWidth: 3, defaultOn: true },
    ];

    // 6m Depth Parameters
    const params6m = [
        { key: 'at6mDegC', label: 'Temperature (°C)', color: '#c30a0aff', strokeWidth: 3, defaultOn: true },
        { key: 'rh6mPer', label: 'Humidity (%)', color: '#1565C0', strokeWidth: 3, defaultOn: true },
        { key: 'rainFallMm', label: 'Rainfall (mm)', color: '#311B92', strokeWidth: 3, defaultOn: true },
        { key: 'avgNetRadiationwBym2', label: 'Net Rad (W/m²)', color: '#eee30dff', strokeWidth: 3, defaultOn: true },
        { key: 'ws6mMps', label: 'Wind Spd (m/s)', color: '#2E7D32', strokeWidth: 3, defaultOn: true },
        { key: 'wd6mDeg', label: 'Wind Dir (°)', color: '#00897B', strokeWidth: 3, defaultOn: true },
    ];

    // Combined Depth Parameters
    const paramsCombined = [
        { key: 'at2mDegC', label: 'Temp (2m) °C', color: '#e53935', strokeWidth: 3, defaultOn: true },
        { key: 'at4mDegC', label: 'Temp (4m) °C', color: '#c30a0aff', strokeWidth: 3, defaultOn: true },
        { key: 'at6mDegC', label: 'Temp (6m) °C', color: '#7f0000', strokeWidth: 3, defaultOn: true },
        { key: 'rh2mPer', label: 'Hum (2m) %', color: '#1E88E5', strokeWidth: 3, defaultOn: true },
        { key: 'rh4mPer', label: 'Hum (4m) %', color: '#1565C0', strokeWidth: 3, defaultOn: true },
        { key: 'rh6mPer', label: 'Hum (6m) %', color: '#0D47A1', strokeWidth: 3, defaultOn: true },
        { key: 'rainFallMm', label: 'Rain (2m) mm', color: '#311B92', strokeWidth: 3, defaultOn: true },
        { key: 'avgNetRadiationwBym2', label: 'Net Rad (2m)', color: '#eee30dff', strokeWidth: 3, defaultOn: true },
        { key: 'ws2mMps', label: 'Wind Spd (2m)', color: '#43A047', strokeWidth: 3, defaultOn: true },
        { key: 'ws4mMps', label: 'Wind Spd (4m)', color: '#2E7D32', strokeWidth: 3, defaultOn: true },
        { key: 'ws6mMps', label: 'Wind Spd (6m)', color: '#106314ff', strokeWidth: 3, defaultOn: true },
        { key: 'wd2mDeg', label: 'Wind Dir (2m)', color: '#4DB6AC', strokeWidth: 3, defaultOn: true },
        { key: 'wd4mDeg', label: 'Wind Dir (4m)', color: '#00897B', strokeWidth: 3, defaultOn: true },
        { key: 'wd6mDeg', label: 'Wind Dir (6m)', color: '#004D40', strokeWidth: 3, defaultOn: true },
        { key: 'ap2mHpa', label: 'Pressure (2m)', color: '#7B1FA2', strokeWidth: 3, defaultOn: true },
    ];

    const stations = (selectedStations && selectedStations.length > 0) ? selectedStations : [];
    const isMulti = stations.length > 1;

    // Use single station logic or multi station logic
    const finalParams2m = isMulti ? generateGroupParams(params2m, stations) : params2m;
    const finalParams4m = isMulti ? generateGroupParams(params4m, stations) : params4m;
    const finalParams6m = isMulti ? generateGroupParams(params6m, stations) : params6m;
    const finalParamsCombined = isMulti ? generateGroupParams(paramsCombined, stations) : paramsCombined;

    const group2m = isMulti ? generateStationGroups(params2m, stations) : null;
    const group4m = isMulti ? generateStationGroups(params4m, stations) : null;
    const group6m = isMulti ? generateStationGroups(params6m, stations) : null;
    const groupCombined = isMulti ? generateStationGroups(paramsCombined, stations) : null;

    if (rawData.length === 0) {
        return (
            <Box sx={{ p: 5, textAlign: 'center' }}>
                <Typography color="text.secondary">No range data available.</Typography>
            </Box>
        );
    }

    const minWidth = isMulti ? Math.max(1200, stations.length * 400) : null;

    return (
        <Box sx={{ padding: theme.spacing(2) }}>
            <Typography
                variant="h4"
                sx={{
                    fontWeight: 700,
                    color: theme.palette.primary.main,
                    marginBottom: theme.spacing(3),
                    textAlign: 'center'
                }}
            >
                Date Range Weather Trends
            </Typography>

            <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: theme.spacing(2) }}>
                <CollapsibleChartGroup
                    title="Weather Trends – 2m Depth"
                    icon="📈"
                    parameters={finalParams2m}
                    paramGroups={group2m}
                    data={rawData}
                    defaultExpanded={true}
                    gradientColors={[theme.palette.primary.main, theme.palette.primary.light]}
                    topBorderColor={theme.palette.primary.main}
                    chartType="candle"
                    trendType="Range"
                    minChartWidth={minWidth}
                    selectionType="radio"
                />

                <CollapsibleChartGroup
                    title="Weather Trends – 4m Depth"
                    icon="📈"
                    parameters={finalParams4m}
                    paramGroups={group4m}
                    data={rawData}
                    defaultExpanded={true}
                    gradientColors={[theme.palette.secondary.main, theme.palette.secondary.light]}
                    topBorderColor={theme.palette.secondary.main}
                    chartType="candle"
                    trendType="Range"
                    minChartWidth={minWidth}
                    selectionType="radio"
                />

                <CollapsibleChartGroup
                    title="Weather Trends – 6m Depth"
                    icon="📈"
                    parameters={finalParams6m}
                    paramGroups={group6m}
                    data={rawData}
                    defaultExpanded={true}
                    gradientColors={['#01383eff', '#015c66']}
                    topBorderColor="#01383eff"
                    chartType="candle"
                    trendType="Range"
                    minChartWidth={minWidth}
                    selectionType="radio"
                />

                <CollapsibleChartGroup
                    title="Combined Depth Trends (2m, 4m, 6m)"
                    icon="📊"
                    parameters={finalParamsCombined}
                    paramGroups={groupCombined}
                    data={rawData}
                    defaultExpanded={true}
                    gradientColors={[theme.palette.info.main, theme.palette.info.light]}
                    topBorderColor={theme.palette.info.main}
                    chartType="candle"
                    trendType="Range"
                    minChartWidth={minWidth}
                    selectionType="radio"
                />
            </Box>
        </Box>
    );
};

export default RangeTrendCandleChart;
